<template>
    <div class="loginContainer">
      <dean-header :logo="headerLogo" logo-height="35"></dean-header>

      <div class="dialogContainer">
        <alert-notification/>
        <dean-dialog
                class="login-dialog"
                title="Inloggen"
                :icon="lockIcon"
                align-top>
          <alert-notification></alert-notification>

          <span class="dean-dialog-logo" slot="dean-dialog-logo">
            <dean-icon class="deanone-logo" :glyph="headerLogo"></dean-icon>
          </span>

          <span class="fields">
            <dean-input-field
                    @enter="loginClick"
                    id="email"
                    name="email"
                    v-model="data.email"
                    required-field="true"
                    type="text"
                    :warning="$v.data.email.$error"
                    @focus="$v.data.email.$touch()">
              {{ this.$t('inputs.email') }}
            </dean-input-field>

            <dean-input-field
                    v-if="showPassword"
                    @enter="loginClick"
                    id="password"
                    name="password"
                    v-model="data.password"
                    required-field="true"
                    type="password"
                    :warning="$v.data.password.$error"
                    @focus="$v.data.password.$touch()">
              {{ this.$t('inputs.password') }}
            </dean-input-field>
          </span>

          <span id="actions">
            <dean-button class="auth-login" primary="true" button-primary dialog-button @dean-button-click="loginClick">Inloggen</dean-button>
            <span v-if="showForgotPassword" class="auth-forgot-password" @click="requestPassword">{{ this.$t('messages.forgotPasswordLink') }}</span>
          </span>
        </dean-dialog>
      </div>
    </div>
</template>

<script lang="ts">
  // VUE
  import {
    Vue,
    Component,
  } from 'vue-property-decorator';

  import {
    deanButton,
    deanHeader,
    lockIcon,
    warningIcon,
    deanIcon,
  } from '@deanone/dean-components';

  import {required, email} from 'vuelidate/lib/validators';
  import {Validations} from 'vuelidate-property-decorators';

  import alertNotification from '@/components/deanComponents/deanNotification.vue';
  import deanDialog from '@/components/deanComponents/deanDialog.vue';
  import deanInputField from '@/components/deanComponents/deanInputField.vue';
  import {createNotification} from '@/mixins/validations';
  import {getErrorFromResponse} from '@/mixins/errorMessage';
  import {redirectToKeycloak} from '@/services/keycloak';

  @Component({
    components: {
      deanInputField,
      deanButton,
      deanDialog,
      deanHeader,
      deanIcon,
      alertNotification,
    },
  })

  /**
   * Class for login.
   *
   * @class      Login (name)
   */
  export default class Login extends Vue {
    public lockIcon: object = lockIcon;
    public warningIcon: object = warningIcon;
    public keycloakEnabled: boolean = process.env.VUE_APP_KEYCLOAK_ENABLED === 'true';
    public needsMigration: boolean = false;
    public data: {email: string | null, password: string | null} = {
      email: null,
      password: null,
    };

    @Validations()
    get validations() {
      const validations: any = {
        data: {
          email: {required, email},
        },
      };
      if (this.showPassword) {
        validations.data.password = {required};
      }
      return validations;
    }

    /**
     * Password field is shown when Keycloak is disabled (classic login)
     * or when the auth.check step determined the user still needs migration.
     */
    get showPassword() {
      return !this.keycloakEnabled || this.needsMigration;
    }

    /**
     * "Forgot your password?" is only relevant for users that don't have a
     * Keycloak account yet (i.e. still need migration), shown on the 2nd step
     * of the login form, alongside the password field.
     */
    get showForgotPassword() {
      return this.keycloakEnabled && this.needsMigration;
    }

    get headerLogo() {
      return this.$store.getters.getTheme.data.loginLogo;
    }

    /**
     * Process login form:
     * 1. If Keycloak is enabled and the migration status is not known yet,
     *    call auth.check (email only) to determine the next step:
     *    - redirect_keycloak -> redirect straight to Keycloak login
     *    - needs_migration   -> reveal the password field and ask the user to submit again
     * 2. Otherwise (Keycloak disabled, or migration needed) call auth.login
     *    with email + password to authenticate/migrate the user.
     */
    public loginClick() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$toast(createNotification(this.$v));
        return;
      }

      this.$store.dispatch('setLoadOverlay', true);

      if (this.keycloakEnabled && !this.needsMigration) {
        this.$store.dispatch('checkUser', this.data.email).then(
            (status: string) => {
              this.$store.dispatch('setLoadOverlay', false);
              if (status === 'needs_migration') {
                this.needsMigration = true;
              } else {
                // redirect_keycloak (or any other known status) -> go straight to Keycloak,
                // do NOT call auth.login here (no password has been provided/needed).
                // Let Keycloak silently reuse an existing SSO session if the user is
                // already logged in there, instead of forcing the login form again.
                redirectToKeycloak(this.data.email as string);
              }
            },
            (error) => {
              this.$store.dispatch('setLoadOverlay', false);
              this.showLoginError(error);
            },
        );
        return;
      }

      this.$store.dispatch('authorizeUser', this.data).then(
          () => {
            if (!this.keycloakEnabled) {
              this.$store.dispatch('setLoadOverlay', false);
              this.$toast({
                text: this.$t('messages.loginSuccess') as string,
                theme: 'success',
              });
              const redirectPath = sessionStorage.getItem('redirect_after_login');
              sessionStorage.removeItem('redirect_after_login');
              if (redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('//')) {
                this.$router.push(redirectPath);
              } else {
                this.$router.push({name: 'dashboard'});
              }
            }
            // if Keycloak is enabled, authorizeUser redirects to Keycloak — nothing to do here
          },
          (error) => {
            this.$store.dispatch('setLoadOverlay', false);
            this.showLoginError(error);
          },
      );
    }

    public created() {
      this.$store.dispatch('updateSideMenu', {});

      if (this.$route.query.loggedOut) {
        this.$toast({
          text: this.$t('messages.logoutSuccess') as string,
          theme: 'success',
        });
        this.$router.replace({ name: 'auth.login' });
      }

      const {email} = this.$route.query;
      if (typeof email === 'string' && email) {
        this.data.email = email;
        this.needsMigration = true;
      }
    }

    /**
     * Navigate to the Keycloak "forgot password" migration page, prefilling the
     * email address already entered on this step.
     */
    public requestPassword() {
      this.$router.push({name: 'auth.forgotPassword', query: {email: this.data.email || undefined}});
    }

    /**
     * Show the actual backend error (400/401/404/422 from auth.login) on the login page
     * itself, instead of redirecting the user to Keycloak/logout or showing a generic message.
     */
    private showLoginError(error: any) {
      const status = error?.response?.status;
      const errorResponse = error?.response?.data;
      const defaultMessage = (status === 404
        ? this.$t('messages.userNotFound')
        : this.$t('messages.invalidCredentials')) as string;

      this.$toast({
        icon: this.warningIcon,
        text: errorResponse ? getErrorFromResponse(errorResponse, defaultMessage) : defaultMessage,
        theme: 'warning',
      });
    }
  }
</script>

<style lang="scss" scoped>
  // IMPORT
  @import '~@deanone/dean-components/src/scss/vars';

  .login-dialog {
    background: var(--color-light-shade-1, $color-light-shade-1);
  }

  .dean-dialog-button-group > span {
    display: flex;
    justify-content: flex-end;
    padding: 5px;
  }

  .forgot-password {
    text-align: right;
    padding: 10px;
    font-size: 8pt;
    color: var(--color-link, $color-link);
    cursor: pointer;
  }

  .login-dialog {
    .dean-dialog-logo {
      display: block;
      text-align: center;
      margin-bottom: 15px;
    }
  }

  .deanone-logo {
    width: 80px;
  }

  .fields {
    display: block;
    padding: 10px 0 15px;
  }

  #actions {
    padding:0 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    .auth-forgot-password {
      text-decoration: underline;
    }
  }
</style>
